import React from 'react';
import { Lock } from 'lucide-react';
import SEO from '../components/SEO';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
      <SEO
        title="Privacy Policy — Jerusalem Holy Prayer"
        description="How Jerusalem Holy Prayer collects, protects, and respects the personal information and prayer content shared by our customers."
        path="/privacy"
      />
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full shadow-xl">
                <Lock className="h-12 w-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-3">Privacy Policy</h1>
            <p className="text-gray-600">Effective Date: May 10, 2026</p>
          </div>

          {/* Content card */}
          <div data-testid="privacy-content" className="bg-white rounded-xl shadow-lg p-8 md:p-12 space-y-10">

            <section>
              <p className="text-gray-700 leading-relaxed">
                At Jerusalem Holy Prayer, we recognize that the prayers and personal information you share
                with us are deeply private and sacred. This policy outlines how we handle your data with
                reverence and security.
              </p>
            </section>

            <section className="border-l-4 border-amber-500 bg-amber-50/60 rounded-r-lg pl-5 py-4">
              <p className="text-gray-800 italic leading-relaxed">
                “Your prayer content is treated as a sacred trust, viewed only by the staff members
                necessary to fulfill your pilgrimage request.”
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 font-serif mb-3">1. Information Collection</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We collect the following information to facilitate your request:
              </p>
              <ul className="list-disc list-outside space-y-2 text-gray-700 ml-6">
                <li><span className="font-semibold text-gray-900">Prayer Content:</span> The text of your intention or prayer.</li>
                <li><span className="font-semibold text-gray-900">Contact Details:</span> Your name and email address for delivery confirmation.</li>
                <li>
                  <span className="font-semibold text-gray-900">Payment Processing:</span> All payments are processed through secure,
                  third-party providers (e.g., Stripe). We do not store credit card numbers on our servers.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 font-serif mb-3">2. Use of Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed">
                We use tools such as the Facebook Pixel and Google Analytics to improve our outreach and
                measure the effectiveness of our advertising. These tools collect usage data in an aggregated,
                non-identifiable manner.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 font-serif mb-3">3. Data Protection &amp; Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                Your digital data is protected using SSL encryption. After the physical delivery has been
                completed and photographic proof has been sent, your prayer content is handled according to
                our secure archiving and respectful disposal protocols.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 font-serif mb-3">4. Third-Party Sharing</h2>
              <p className="text-gray-700 leading-relaxed">
                We do not sell or trade your personal information. We only share data with essential service
                providers (e.g., payment processors and email delivery systems) required to complete your order.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 font-serif mb-3">5. Your Rights</h2>
              <p className="text-gray-700 leading-relaxed">
                You have the right to request the deletion of your contact information from our database at
                any time by contacting our support team.
              </p>
            </section>

            <section className="pt-6 border-t border-amber-100">
              <p className="text-gray-700 leading-relaxed mb-2">
                Questions about your data or a deletion request? Reach our support team at{' '}
                <a
                  href="mailto:info@jerusalemholyprayer.org"
                  className="text-amber-700 font-semibold hover:text-amber-800 underline underline-offset-2"
                  data-testid="privacy-contact-email"
                >
                  info@jerusalemholyprayer.org
                </a>
                . We typically respond within 24 hours.
              </p>
              <p className="text-center text-sm text-amber-700 font-semibold tracking-wide mt-6">
                Jerusalem Holy Prayer · Secure &amp; Sacred Data Management
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
